import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { toAbsoluteUrl } from '../../../_metronic/helpers';
import { oauthCallbackPath, parseNuvemshopMigrationState } from '../platform-migration/api';
import { onboardingOAuthCallbackPath, parseNuvemshopOnboardingState } from './api';

/**
 * Página de decisão após callback da Nuvemshop
 * 
 * Pergunta ao usuário: "Você já é cliente Konfidency?"
 * - SIM: Salva code em sessionStorage e redireciona para login
 * - NÃO: Redireciona para página com Calendly
 */
export function NuvemshopCallbackPage() {
  const location = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(location.search);
  const code = query.get('code');
  // State de migração de plataforma: o code é da loja NOVA e vai para a migração do cliente que
  // está no state. Nunca para o fluxo abaixo, que troca o token da loja atual do cliente ativo.
  const migration = parseNuvemshopMigrationState(query.get('state'));
  // State de onboarding: o code é da loja do cliente em onboarding, que conecta pelo assistente
  const onboarding = parseNuvemshopOnboardingState(query.get('state'));
  const redirecting = !!migration || !!onboarding;

  React.useEffect(() => {
    if (!code) return;
    if (onboarding) {
      window.location.replace(`${onboardingOAuthCallbackPath('nuvemshop', onboarding.customer, onboarding.state)}?code=${encodeURIComponent(code)}`);
      return;
    }
    if (!migration) return;
    window.location.replace(`${oauthCallbackPath('nuvemshop', migration.customer, migration.state)}?code=${encodeURIComponent(code)}`);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Se não tem code, redirecionar para home
  React.useEffect(() => {
    if (!code) {
      console.warn('[NUVEMSHOP] Callback sem code, redirecionando...');
      history.push('/');
    }
  }, [code, history]);

  const handleExistingClient = () => {
    if (!code) return;
    
    console.log('[NUVEMSHOP] Cliente existente, salvando code e redirecionando para login');
    
    // Salvar code no sessionStorage para processar após login
    sessionStorage.setItem('nuvemshop_code', code);
    
    // Redirecionar direto para a página de destino
    // O Auth0 vai interceptar e fazer login, depois retornar para essa página
    window.location.href = '/review-settings/integrations/platform';
  };

  const handleNewClient = () => {
    if (!code) return;
    
    console.log('[NUVEMSHOP] Novo cliente, redirecionando para Calendly');
    
    // Redirecionar para página com Calendly (que salvará o token)
    history.push(`/nuvemshop/agendar?code=${code}`);
  };

  if (!code || redirecting) {
    return (
      <div className='d-flex flex-column flex-root'>
        <div className='d-flex flex-column flex-column-fluid text-center p-10'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Carregando...</span>
          </div>
          <p className='text-gray-600 fs-4 fw-bold mt-5'>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='d-flex flex-column flex-root'>
      <div
        className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'
        style={{
          backgroundImage: `url('${toAbsoluteUrl('/media/illustrations/sketchy-1/14.png')}')`,
        }}
      >
        {/* Logo */}
        <div className='d-flex flex-center flex-column flex-column-auto p-10 pt-lg-20'>
          <a href='/' className='mb-12'>
            <img
              alt='Konfidency Logo'
              src={toAbsoluteUrl('/media/logos/logo-2-dark.svg')}
              className='h-50px'
            />
          </a>
          <div className='d-block text-center'>
            <img src={toAbsoluteUrl('/media/logos/nuvemshop_logo.png')} className='h-100px' />
          </div>
        </div>

        {/* Content */}
        <div className='d-flex flex-column flex-column-fluid text-center p-10 py-lg-10'>
          <div className='pt-lg-10 mb-10'>
            {/* Card */}
            <div className='card shadow-sm mx-auto' style={{ maxWidth: '500px' }}>
              <div className='card-body p-10'>
                {/* Header */}
                <div className='text-center mb-10 mt-10'>
                  <h1 className='fw-bolder text-gray-900 mb-5 fs-2x'>
                    Você está a um passo de ter Konfidency Reviews na sua loja Nuvemshop!
                  </h1>
                  <p className='text-gray-600 fs-5 fw-semibold'>
                    Você já é cliente Konfidency?
                  </p>
                </div>

                {/* Botões de decisão */}
                <div className='d-flex flex-column gap-4'>
                  <button
                    onClick={handleExistingClient}
                    className='btn btn-lg btn-primary fw-bold'
                    type='button'
                  >
                    Sim, já sou cliente
                  </button>

                  <button
                    onClick={handleNewClient}
                    className='btn btn-lg btn-success fw-bold'
                    type='button'
                  >
                    Não, quero conhecer
                  </button>
                </div>

                {/* Footer info */}
                <div className='mt-10 pt-8 border-top border-gray-300'>
                  <p className='text-muted fs-7 mb-0'>
                    Ao continuar, você concorda com nossa{' '}
                    <a
                      href='https://www2.konfidency.cx/privacy-policy'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-primary fw-bold'
                    >
                      Política de Privacidade
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className='d-flex flex-center flex-column-auto p-10'>
          <div className='d-flex align-items-center fw-bold fs-6'>
            <a
              href='https://www.konfidency.cx'
              className='text-muted text-hover-primary px-2'
            >
              Sobre a Konfidency
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
